import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { get } from 'lodash-es';
import { from } from 'rxjs';

import {
  NEEDS_ATTENTION,
  STATUS_AWAITING_SHIPMENT,
  STATUS_COMPLETED,
  STATUS_FLAGGED,
  STATUS_OUTSTANDING,
  STATUS_PENDING_APPROVAL,
  STATUS_PENDING_PAYMENT,
  STATUS_PROCESSING,
  STATUS_RECEIVED,
  STATUS_VOIDED,
} from './returns.helpers';
import { emptyArray } from 'ui-components/default-props/empty';
import { appendExtraParamsWithPossibleKeyDupes } from '../../services/api/grid-api-extra-params-service';
import { PAGE_SIZE_250, FIRST_PAGE } from '../../services/pagination-service';

const getContractEnumMap = {
  [STATUS_PENDING_APPROVAL]: 'PENDING_APPROVAL',
  [STATUS_PENDING_PAYMENT]: 'PENDING_PAYMENT',
  [STATUS_COMPLETED]: 'COMPLETED',
  [NEEDS_ATTENTION]: 'NEEDS_ATTENTION',
  [STATUS_AWAITING_SHIPMENT]: 'AWAITING_SHIPMENT',
  [STATUS_PROCESSING]: 'PROCESSING',
  [STATUS_FLAGGED]: 'NEEDS_ATTENTION',
};

// Backend's ReturnGridQueryRequest.ReturnStatus is a C# enum (Outstanding/Received/Voided) bound
// via Newtonsoft's StringEnumConverter, which is case-sensitive on read - must match exactly.
const returnStatusContractEnumMap = {
  [STATUS_OUTSTANDING]: 'Outstanding',
  [STATUS_RECEIVED]: 'Received',
  [STATUS_VOIDED]: 'Voided',
};

export const cancelRequest = async (returnId, storeGuid) => {
  const response = await axios.put('/enhancedreturns/cancelRequest', {
    returnId,
    storeGuid,
  });
  return response.data;
};

export const processReturn = async (returnId, storeGuid, returnExchangesInfo = {}) => {
  const response = await axios.put('/enhancedreturns/processReturn', {
    returnId,
    storeGuid,
    returnExchangesInfo,
  });
  return response.data;
};

export const approveReturn = async (returnId, storeGuid, returnExchanges = []) => {
  const response = await axios.put('/enhancedreturns/approveReturn', {
    returnId,
    storeGuid,
    returnExchanges,
  });
  return response.data;
};

export const retryReturn = async (returnId, storeGuid) => {
  const response = await axios.put('/enhancedreturns/retryRequest', {
    returnId,
    storeGuid,
  });
  return response.data;
};

export const rejectReturn = async (returnId, storeGuid, returnExchangeIds = []) => {
  const response = await axios.put('/enhancedreturns/rejectReturn', {
    returnId,
    storeGuid,
    returnExchangeIds,
  });
  return response.data;
};

export const sendReturnPaymentReminder = async (returnId, storeGuid, emails = []) => {
  const response = await axios.post('/enhancedreturns/sendPaymentReminder', {
    returnId,
    storeGuid,
    emails,
  });
  return response.data;
};

export const sendLabelToBuyer = async (returnId, storeGuid, emails = []) => {
  const response = await axios.post('/enhancedreturns/sendLabelToBuyer', {
    returnId,
    storeGuid,
    emails,
  });
  return response.data;
};

export const getEnhancedReturnsCountForStore = (storeGuid, searchTerm) => {
  const params = new URLSearchParams();
  if (searchTerm) {
    params.set('searchTerm', searchTerm);
  }
  const query = params.toString();
  const route = `/enhancedreturns/${storeGuid}/counts${query ? `?${query}` : ''}`;

  return axios
    .get(route)
    .then((response) => ({ storeGuid, ...response.data }))
    .catch(() => ({
      storeGuid,
      statusCountMap: {},
      totalCount: 0,
    }));
};

export const getEnhancedReturnsCounts = async (storeGuids, searchTerm = null) => {
  const allStoreResults = await Promise.all(storeGuids.map((g) => getEnhancedReturnsCountForStore(g, searchTerm)));
  return allStoreResults;
};

export const getEnhancedReturnsSellerCounts = async (searchTerm = null, filteredStoreGuids = []) => {
  const params = new URLSearchParams();
  if (searchTerm) {
    params.set('searchTerm', searchTerm);
  }
  if (filteredStoreGuids.length > 0) {
    params.set('storeGuids', filteredStoreGuids.join(','));
  }
  const query = params.toString();
  const route = `/enhancedreturns/seller/counts${query ? `?${query}` : ''}`;

  return axios
    .get(route)
    .then((response) => response.data)
    .catch(() => ({ statusCountMap: {}, totalCount: 0 }));
};

export const getEnhancedReturns = async (storeGuid, statusView, page = FIRST_PAGE, pageSize = PAGE_SIZE_250, extraFilters = {}) => {
  const payload = {
    storeGuid,
    statusView: getContractEnumMap[statusView],
    page,
    pageSize,
    ...extraFilters,
  };

  const response = await axios.post('/enhancedreturns/grid', payload);
  const result = response?.data ?? {};
  return result;
};

export const getEnhancedReturnsSellerGrid = async (statusView, page = FIRST_PAGE, pageSize = PAGE_SIZE_250, extraFilters = {}) => {
  const payload = {
    statusView: getContractEnumMap[statusView],
    page,
    pageSize,
    ...extraFilters,
  };

  const response = await axios.post('/enhancedreturns/seller/grid', payload);
  return response?.data ?? {};
};

export const getReturnsForSalesOrder = async (orderNumber, storeGuid) => {
  const encodedOrderNumber = encodeURIComponent(orderNumber);
  const response = await axios.get(`/enhancedreturns/${storeGuid}/salesorder/${encodedOrderNumber}/returns`);
  return response.data;
};

export const getReturnDetails = async (storeGuid, returnId) => {
  // When creating a new return, returnId can be 'new' and we don't want
  // to fetch data in that case.
  if (returnId === 'new') {
    return {};
  }

  const response = await axios.get(`/enhancedreturns/${storeGuid}/returnDetails/${returnId}`);
  return response.data;
};

/**
 * Creates an internal note for a return request
 * @param {string} storeGuid - The store GUID
 * @param {string} returnId - The ID of the return
 * @param {string} note - The note text to add
 * @returns {Promise<import('../../orders/order-details/enhanced-return/enhanced-return-types').Note>} - Promise resolving to the newly created note record
 */
export const createReturnNote = async (storeGuid, returnId, note) => {
  const response = await axios.post(`/enhancedreturns/${storeGuid}/returnDetails/${returnId}/notes`, {
    note,
  });
  return get(response, 'data', null);
};

/**
 * Deletes an internal note for a return request
 * @param {string} storeGuid - The store GUID
 * @param {string} returnId - The ID of the return
 * @param {string} noteId - The ID of the note to delete
 * @returns {Promise} - Promise resolving when the note is deleted
 */
export const deleteReturnNote = async (storeGuid, returnId, noteId) => {
  const response = await axios.delete(`/enhancedreturns/${storeGuid}/returnDetails/${returnId}/notes/${noteId}`);
  return get(response, 'data', null);
};

export const getRetailerConfiguration = async (storeGuid) => {
  try {
    const response = await axios.get(`/enhancedreturns/${storeGuid}/retailer/configuration`);
    return response.data;
  } catch {
    return [];
  }
};

export const getSSRSingleSignOnToken = async (storeGuid) => {
  const response = await axios.post(`/enhancedreturns/${storeGuid}/ssrSingleSignOn`);
  return response.data;
};

export const updateEnhancedReturnsStoreStatus = async (storeGuid) => {
  const response = await axios.put(`/enhancedreturns/${storeGuid}/updateStatus`);
  return response.data;
};

export const useUpdateEnhancedReturnsStoreStatusMutation = (options = {}) =>
  useMutation({
    mutationFn: (storeGuid) => updateEnhancedReturnsStoreStatus(storeGuid),
    ...options,
  });

const makeParams = (viewId, page, pageSize, extraParams) => {
  let params = {
    page,
    pageSize,
  };
  if (viewId in returnStatusContractEnumMap) {
    params.returnStatus = returnStatusContractEnumMap[viewId];
  }
  if (extraParams) {
    params = appendExtraParamsWithPossibleKeyDupes(params, extraParams);
  }

  // Note: Horribly gross. But we don't have much capacity to do the "real" fix
  // The "real" fix is to modernize the returns grid FE and BE to match similar filtering paradigms
  // But engineering doesn't have the appetite for it especially with the longevity of the standard returns grid in question
  if (params.labelProviderId && !Array.isArray(params.labelProviderId)) {
    params.labelProviderId = [params.labelProviderId];
  }

  // The backend contract's OrderBy is a List<ReturnGridOrderBy>. The query-string binder used to accept
  // a bare value for a list property, but strict JSON body binding does not - a mismatched orderBy shape
  // fails deserialization of the whole request body, so it must be sent as an array.
  if (params.orderBy && !Array.isArray(params.orderBy)) {
    params.orderBy = [params.orderBy];
  }

  // Same as orderBy/labelProviderId above: a single applied date filter produces one object rather
  // than an array, but the backend's DateTimeFilters is a List<ReturnGridDateTimeFilter>.
  if (params.dateTimeFilters && !Array.isArray(params.dateTimeFilters)) {
    params.dateTimeFilters = [params.dateTimeFilters];
  }

  return params;
};

/**
 * @param {string} shipmentStatus
 * @param {number} [page]
 * @param {number} [pageSize]
 * @param {Array<Object>} [extraParams] - Array of single-key objects, e.g. [{ orderBy: 'CreatedDateTimeDesc' }]
 * @returns {Observable}
 */
export const getReturns = (shipmentStatus, page = FIRST_PAGE, pageSize = PAGE_SIZE_250, extraParams = undefined) => {
  const payload = makeParams(shipmentStatus, page, pageSize, extraParams);
  const request = axios.post('/returngrid', payload).then((response) => {
    const pageData = get(response, 'data.pageData', emptyArray);
    return {
      ...response.data,
      pageData: pageData.map((row) =>
        Object.keys(row).reduce(
          (newRow, key) => ({
            ...newRow,
            [key]: Array.isArray(row[key]) ? (row[key][0] ?? '') : row[key],
          }),
          {},
        ),
      ),
    };
  });
  return from(request);
};

export const exportReturns = (returnStatus, extraParams) => {
  const params = makeParams(returnStatus, undefined, undefined, extraParams);

  const request = axios.post('/returns/export', params);

  return request.then((response) => response.data);
};

export const getReturnsSearchTermCount = (searchData) => {
  const params = new URLSearchParams();
  params.append('searchTerm', searchData.searchTerm.trim());

  const returns = axios.get(`/returngrid/quicksearch/statuses?${params}`).then((response) => response.data);
  return from(returns);
};

export const getReturnReasons = () => {
  const request = axios.get('/return/reasons').then((response) => {
    return get(response, 'data', undefined);
  });

  const data$ = from(request);
  return data$;
};

export const setReceivedDateOnReturn = (fulfillmentIds, receivedDateTime) => {
  const post = axios.post('/return/setReceived', { fulfillmentIds, receivedDateTime }).then((response) => {
    return get(response, 'data', undefined);
  });
  const update$ = from(post);
  return update$;
};

/**
 * @typedef EmailReturnLabelRequest
 * @property {string} fulfillmentId
 * @property {string} emails - This is a single email address, even though it's plural
 */

/**
 *
 * @param {string} returnFulfillmentId
 * @param {string[]} emailAddresses
 * @returns {EmailReturnLabelRequest[]}
 */
export const createPostEmailsForReturnRequest = (returnFulfillmentId, emailAddresses) => {
  return emailAddresses.map((emailAddress) => ({
    fulfillmentId: returnFulfillmentId,
    emails: emailAddress,
  }));
};

/**
 * @param {EmailReturnLabelRequest[]} requests
 * @returns {Promise<{ jobGuids: string[] }>}
 */
export const postEmailsForReturnLabelPromise = (requests) => {
  return axios.post('/bulk/email/returnLabel', requests).then((response) => response?.data);
};

/**
 * @param {EmailReturnLabelRequest[]} requests
 * @returns {Observable}
 */
export const postEmailsForReturnLabel = (requests) => {
  const post$ = from(postEmailsForReturnLabelPromise(requests));
  return post$;
};

export const getReturnsFromQuickSearch = (searchData) => {
  const params = new URLSearchParams();
  params.append('page', searchData.page);
  params.append('pageSize', searchData.pageSize);
  params.append('searchTerm', searchData.searchTerm.trim());
  const returnStatus = returnStatusContractEnumMap[searchData.returnStatus];
  if (returnStatus) {
    params.append('returnStatus', returnStatus);
  }

  const search = axios.get(`returngrid/quicksearch?${params}`).then((response) => response.data);
  return from(search);
};

/**
 * @param {object} args
 * @param {object} args.shippingConfiguration
 * @param {string[]} [args.emailTo]
 * @param {string} [args.rmaNumber]
 * @param {string} args.returnId - SsrReturnGuid identifying the return
 * @param {string} args.salesOrderId - Identifies the sales order; the server resolves the store from it
 * @param {string} [args.displayScheme]
 * @param {boolean} [args.downloadLabelNow]
 * @param {number} [args.returnedItemsValue]
 * @returns {Promise}
 */
export const createReturnLabelPromise = ({
  shippingConfiguration,
  emailTo = [],
  rmaNumber = null,
  returnId,
  salesOrderId,
  displayScheme = 'label',
  downloadLabelNow = false,
  returnedItemsValue = null,
} = {}) => {
  const payload = {
    shippingConfiguration,
    emailTo,
    rmaNumber,
    ssrReturnGuid: returnId,
    salesOrderId,
    displayScheme,
    downloadLabelNow,
    returnedItemsValue,
  };
  return axios.post('/return/shipstationReturnLabel', payload);
};

export const createReturnLabel = (shippingConfiguration, emailTo, rmaNumber, displayScheme) => {
  const post = axios.post('/return/createLabel', { shippingConfiguration, emailTo, rmaNumber, displayScheme }).then((response) => {
    return {
      fulfillmentId: response.data.fulfillmentId,
    };
  });
  const update$ = from(post);
  return update$;
};

export const activateEnhancedReturns = () => {
  return axios.post('/enhancedreturns/enable').then((response) => response.data);
};

export const activateStore = (storeGuid, payload) => {
  return axios.post(`/enhancedreturns/${storeGuid}/activate`, payload).then((response) => response.data);
};

export const deactivateEnhancedReturnsForStore = (storeGuid) => {
  return axios.put(`/enhancedreturns/${storeGuid}/deactivate`).then((response) => response.data);
};

export const generateNativeStoreAuthenticationUrl = (storeGuid, redirectUrl, useMonoAuthCredentials) => {
  return axios
    .post(`/enhancedreturns/${storeGuid}/generateNativeStoreAuthUrl`, {
      redirectUrl,
      useMonoAuthCredentials,
    })
    .then((response) => response.data);
};

export const useGenerateNativeStoreAuthenticationUrlMutation = () =>
  useMutation({
    mutationFn: ({ storeGuid, redirectUrl, useMonoAuthCredentials }) =>
      generateNativeStoreAuthenticationUrl(storeGuid, redirectUrl, useMonoAuthCredentials),
  });

export const getReturnShipmentConfiguration = async (storeGuid, returnId) => {
  const response = await axios.get(`/enhancedreturns/${storeGuid}/returnDetails/${returnId}/returnShipmentConfiguration`);
  return response.data;
};

export const fetchReturnLocations = (storeGuid) => {
  return axios.get(`/enhancedreturns/${storeGuid}/returnLocations`).then((response) => response.data);
};

export const setDefaultReturnLocation = (storeGuid, locationIdExternal) => {
  return axios
    .put(`/enhancedreturns/${storeGuid}/returnLocations/default`, {
      locationIdExternal,
    })
    .then((response) => response.data);
};

export const fetchReturnCarriers = (storeGuid) => {
  return axios.get(`/enhancedreturns/${storeGuid}/carriers`).then((response) => response.data);
};

export const updateReturnServiceMethod = (storeGuid, data) => {
  return axios.put(`/enhancedreturns/${storeGuid}/defaultReturnServiceMethod`, data).then((response) => response.data);
};
